import {ElementRef} from '@angular/core';
import {Action, AdaptiveCard, HostConfig, OpenUrlAction, SubmitAction} from 'adaptivecards';
import * as ACData from 'adaptivecards-templating';

/**
 * Configuration for adaptive card action callbacks.
 */
export interface AdaptiveCardActionCallbacks {
  onBack: () => void;
  onNextOrFinish: (data: Record<string, string>) => void;
  onClose: () => void;
}

/**
 * Step detail shape expected by the renderer.
 * Both TaskStepAdaptiveCardViewModel and RequestStepAdaptiveCardViewModel satisfy this.
 */
export interface AdaptiveCardStepDetail {
  template: string;
  data: string;
}

/**
 * Renders an adaptive card into the given container element.
 *
 * Handles template expansion, host config, action routing (Back / Next / Finish / Close / OpenUrl),
 * and DOM attachment. The caller provides callbacks for each action type.
 *
 * @param stepDetail  The step containing `template` and `data` JSON strings.
 * @param container   The ElementRef whose nativeElement will receive the rendered card.
 * @param isReadonly  Whether the current task/request is in readonly mode.
 * @param callbacks   Action callbacks for Back, Next/Finish, and Close.
 */
export function renderAdaptiveCard(
  stepDetail: AdaptiveCardStepDetail,
  container: ElementRef,
  isReadonly: boolean,
  callbacks: AdaptiveCardActionCallbacks
): void {
  try {
    const templatePayload = JSON.parse(stepDetail.template || '{}');
    const template = new ACData.Template(templatePayload);
    const cardPayload = template.expand({$root: JSON.parse(stepDetail.data || '{}')});

    const adaptiveCard = new AdaptiveCard();

    adaptiveCard.hostConfig = new HostConfig({
      fontFamily: 'Lato, sans-serif'
    });

    adaptiveCard.onExecuteAction = (action: Action) => {
      if (action instanceof SubmitAction) {
        switch (action.title) {
          case 'Back':
            callbacks.onBack();
            break;
          case 'Next':
          case 'Finish':
            if (isReadonly) {
              callbacks.onNextOrFinish({});
            } else {
              callbacks.onNextOrFinish(action.data as Record<string, string>);
            }
            break;
          case 'Close':
            callbacks.onClose();
            break;
        }
      } else if (action instanceof OpenUrlAction) {
        const url = action.url ?? '';
        if (url.startsWith('https://') || url.startsWith('http://')) {
          globalThis.open(url);
        }
      }
    };

    adaptiveCard.parse(cardPayload);

    container.nativeElement.append(adaptiveCard.render());
  } catch (error) {
    console.error('Failed to render adaptive card step.', error);
  }
}

const TEMPLATE_EXPRESSION_PATTERN = /^\$\{[^}]+\}$/;
export const ADAPTIVE_CARD_TRANSPORT_ENCODED_URL_PREFIX = '__PORTAL_URL_ENCODED__:';
const URL_PROTOCOL_PATTERN = /^https?:\/\//i;

export function prepareAdaptiveCardDataForTransport(data: Record<string, string>): Record<string, string> {
  const sanitizedData = sanitizeAdaptiveCardData(data);
  const preparedData: Record<string, string> = {};

  for (const [key, value] of Object.entries(sanitizedData)) {
    preparedData[key] = URL_PROTOCOL_PATTERN.test(value)
      ? `${ADAPTIVE_CARD_TRANSPORT_ENCODED_URL_PREFIX}${encodeURIComponent(value)}`
      : value;
  }

  return preparedData;
}

/**
 * Sanitizes action data by replacing unresolved template expressions (e.g., ${FIELDID})
 * with empty strings. Prevents expression injection similar to CVE-2022-26134 patterns
 * by ensuring literal template expressions are not sent to the backend.
 *
 * Note: ACData.Template.expand() only works on Adaptive Card JSON structures,
 * not plain key-value objects, so we use regex-based sanitization instead.
 */
export function sanitizeAdaptiveCardData(data: Record<string, string>): Record<string, string> {
  if (!data) {
    return {};
  }

  const sanitized: Record<string, string> = {};

  for (const [key, value] of Object.entries(data)) {
    if (typeof value === 'string' && TEMPLATE_EXPRESSION_PATTERN.test(value)) {
      sanitized[key] = '';
    } else {
      sanitized[key] = value;
    }
  }

  return sanitized;
}
